/**
 * The BOLD theme's kit: the wordmark, the can, carbonation, the pointer effects (fizz, spotlight,
 * tilt), and the two theme controls — flavour and light/dark.
 *
 * The decorative pieces carry no information. Each is `aria-hidden`, ignores the pointer, and sits
 * behind the content it decorates — a screen reader, a keyboard user and a `prefers-reduced-motion`
 * operator all get exactly the admin they had before. The CSS half lives in src/styles/index.css
 * under the `bold-` prefix.
 */
import {
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { motion, useMotionValue, useReducedMotion, useSpring } from 'framer-motion';
import { Moon, Sun } from 'lucide-react';
import { cn } from '@most/ui';

/** "most", set like the can's logo: lowercase, heavy, with a flavour-coloured ring for the "o". */
export function BoldLogo({ className }: { className?: string }) {
  return (
    <span
      className={cn('inline-flex items-center font-logo font-black leading-none tracking-[-0.06em]', className)}
    >
      m<span className="bold-o" aria-hidden="true" />
      st
    </span>
  );
}

/**
 * A tiny deterministic PRNG. `Math.random()` in render would hand every re-render a new scatter of
 * bubbles (and StrictMode a different one on its second pass); a seeded sequence keeps a given
 * <Bubbles> identical for its whole life.
 */
function seeded(seed: number) {
  let s = seed;
  return () => {
    s = (s * 16807) % 2147483647;
    return (s - 1) / 2147483646;
  };
}

/**
 * Carbonation rising through whatever box this is dropped into. The parent must be `relative`
 * (and usually `overflow-hidden`); `rise` is how far a bubble travels before it fades, in px.
 */
export function Bubbles({
  count = 12,
  rise = 320,
  seed = 7,
  className,
}: {
  count?: number;
  rise?: number;
  seed?: number;
  className?: string;
}) {
  const bubbles = useMemo(() => {
    const rnd = seeded(seed);
    return Array.from({ length: count }, () => {
      const size = 3 + rnd() * 9;
      return {
        '--x': `${(rnd() * 96 + 2).toFixed(1)}%`,
        '--s': `${size.toFixed(1)}px`,
        // Small bubbles are fast, big ones lumber — the same as in a glass.
        '--d': `${(4 + size * 0.5 + rnd() * 3).toFixed(1)}s`,
        '--delay': `${(-rnd() * 10).toFixed(1)}s`,
        '--sway': `${(rnd() * 16 - 8).toFixed(0)}px`,
        '--o': (0.2 + rnd() * 0.4).toFixed(2),
        '--rise': `${rise}px`,
      } as CSSProperties;
    });
  }, [count, rise, seed]);

  return (
    <div
      aria-hidden="true"
      className={cn('pointer-events-none absolute inset-0 overflow-hidden', className)}
    >
      {bubbles.map((style, i) => (
        <span key={i} className="bold-bubble" style={style} />
      ))}
    </div>
  );
}

/** The can. Scale it with a font-size (`text-[10px]` → 100 × 220 px). */
export function BoldCan({ className, style }: { className?: string; style?: CSSProperties }) {
  return (
    <div aria-hidden="true" className={cn('bold-can', className)} style={style}>
      <div className="bold-can__drops bold-drops" />
      <span className="bold-can__stripe left-[0.7em]" />
      <span className="bold-can__stripe right-[0.7em]" />
      <span className="bold-can__word">
        m<span className="bold-o" />
        st
      </span>
    </div>
  );
}

/** The loading state: a can being shaken, fizzing. Decorative — pair it with real `aria-busy`. */
export function CanLoader({ className }: { className?: string }) {
  return (
    <div aria-hidden="true" className={cn('relative flex h-40 w-40 items-end justify-center', className)}>
      <Bubbles count={9} rise={120} seed={3} />
      <BoldCan className="bold-shake text-[4.5px]" />
    </div>
  );
}

/**
 * The click fizz: pressing a PRIMARY button pops a burst of bubbles out of it, as if the can had
 * just been opened. One document listener rather than a wrapper on every button, so the shared
 * `Button` in packages/ui (which the buyer and producer apps also use) stays untouched.
 *
 * It is driven by the Web Animations API on throwaway nodes outside React's tree, so a burst can
 * never trigger a render or outlive its own animation.
 */
export function FizzLayer() {
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const layer = document.createElement('div');
    layer.setAttribute('aria-hidden', 'true');
    layer.style.cssText = 'position:fixed;inset:0;pointer-events:none;z-index:9999;overflow:hidden';
    document.body.appendChild(layer);

    const onClick = (e: MouseEvent) => {
      const el = (e.target as Element | null)?.closest?.('button, a, [role="button"]');
      // Keyboard activation reports 0,0 — fizz from the control's centre instead.
      if (!el || !el.classList.contains('bg-primary')) return;
      const box = el.getBoundingClientRect();
      const x = e.clientX || box.left + box.width / 2;
      const y = e.clientY || box.top + box.height / 2;

      for (let i = 0; i < 12; i += 1) {
        const bubble = document.createElement('span');
        const size = 4 + Math.random() * 9;
        bubble.style.cssText =
          `position:absolute;left:${x - size / 2}px;top:${y - size / 2}px;width:${size}px;height:${size}px;` +
          'border-radius:9999px;border:1.5px solid hsl(var(--primary));' +
          'background:radial-gradient(circle at 30% 30%,rgb(255 255 255/.7),rgb(255 255 255/.05) 65%)';
        layer.appendChild(bubble);

        const dx = (Math.random() - 0.5) * 90;
        const dy = -(30 + Math.random() * 80);
        bubble
          .animate(
            [
              { transform: 'translate(0,0) scale(.4)', opacity: 0.95 },
              { transform: `translate(${dx * 0.6}px,${dy * 0.6}px) scale(1)`, opacity: 0.8, offset: 0.5 },
              { transform: `translate(${dx}px,${dy}px) scale(1.25)`, opacity: 0 },
            ],
            { duration: 500 + Math.random() * 400, easing: 'cubic-bezier(.16,1,.3,1)' },
          )
          .finished.finally(() => bubble.remove());
      }
    };

    document.addEventListener('click', onClick);
    return () => {
      document.removeEventListener('click', onClick);
      layer.remove();
    };
  }, []);

  return null;
}

/**
 * Feeds the card spotlight (`.bold-spot` / `.adm-card` in index.css): one passive listener that
 * tells whichever card is under the cursor where the cursor is. The glow itself is pure CSS.
 */
export function SpotlightLayer() {
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const onMove = (e: PointerEvent) => {
      const card = (e.target as Element | null)?.closest?.<HTMLElement>('.bold-spot, .adm-card');
      if (!card) return;
      const box = card.getBoundingClientRect();
      card.style.setProperty('--mx', `${e.clientX - box.left}px`);
      card.style.setProperty('--my', `${e.clientY - box.top}px`);
    };

    document.addEventListener('pointermove', onMove, { passive: true });
    return () => document.removeEventListener('pointermove', onMove);
  }, []);

  return null;
}

/**
 * A card that leans toward the cursor, a few degrees, on a spring — the difference between a
 * rectangle and an object. `max` is the lean at the card's edge, in degrees.
 */
export function Tilt({
  children,
  className,
  max = 5,
}: {
  children: ReactNode;
  className?: string;
  max?: number;
}) {
  const reduce = useReducedMotion();
  const rx = useSpring(useMotionValue(0), { stiffness: 220, damping: 20 });
  const ry = useSpring(useMotionValue(0), { stiffness: 220, damping: 20 });

  if (reduce) return <div className={className}>{children}</div>;

  const onMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const box = e.currentTarget.getBoundingClientRect();
    ry.set(((e.clientX - box.left) / box.width - 0.5) * 2 * max);
    rx.set(-((e.clientY - box.top) / box.height - 0.5) * 2 * max);
  };
  const onLeave = () => {
    rx.set(0);
    ry.set(0);
  };

  return (
    <motion.div
      className={className}
      style={{ rotateX: rx, rotateY: ry, transformPerspective: 900 }}
      onPointerMove={onMove}
      onPointerLeave={onLeave}
    >
      {children}
    </motion.div>
  );
}

/**
 * Liquid along the bottom of a box: two sine layers sliding at different speeds. Each path is
 * four identical periods drawn at 200% width, so a -50% slide lands exactly on itself.
 */
export function Wave({ className }: { className?: string }) {
  const path = 'M0 22 Q150 2 300 22 T600 22 T900 22 T1200 22 T1500 22 T1800 22 T2100 22 T2400 22 V60 H0Z';
  return (
    <div aria-hidden="true" className={cn('pointer-events-none absolute inset-x-0 bottom-0 overflow-hidden', className)}>
      <svg className="bold-wave opacity-25" viewBox="0 0 2400 60" preserveAspectRatio="none" style={{ '--d': '13s' } as CSSProperties}>
        <path d={path} />
      </svg>
      <svg className="bold-wave opacity-40" viewBox="0 0 2400 60" preserveAspectRatio="none" style={{ '--d': '8s', height: '70%' } as CSSProperties}>
        <path d={path} />
      </svg>
    </div>
  );
}

/* ── Flavours ─────────────────────────────────────────────────────────────────────────────── */

const FLAVORS = [
  { id: 'black', label: 'Black', swatch: 'hsl(357 77% 52%)' },
  { id: 'berry', label: 'Berry', swatch: 'hsl(275 70% 54%)' },
  { id: 'mango', label: 'Mango', swatch: 'hsl(32 100% 50%)' },
  { id: 'zero', label: 'Zero', swatch: 'hsl(224 82% 56%)' },
] as const;

type Flavor = (typeof FLAVORS)[number]['id'];

type Theme = 'dark' | 'light';

const FLAVOR_KEY = 'most.admin.flavor';
const THEME_KEY = 'most.admin.theme';

function readStored(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    // Private mode / blocked storage — the default is a fine answer.
    return null;
  }
}

function store(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    // See readStored — the choice simply does not survive a reload.
  }
}

function readFlavor(): Flavor {
  const saved = readStored(FLAVOR_KEY);
  return FLAVORS.some((f) => f.id === saved) ? (saved as Flavor) : 'black';
}

/** Dark unless the operator has chosen otherwise — the black can is the brand's default. */
function readTheme(): Theme {
  return readStored(THEME_KEY) === 'light' ? 'light' : 'dark';
}

/**
 * Puts the saved flavour and theme on <html> BEFORE React renders. Called from main.tsx; without
 * it a light-theme operator would watch the app paint dark and then flip on every load.
 */
export function initBoldTheme() {
  document.documentElement.dataset.flavor = readFlavor();
  document.documentElement.dataset.theme = readTheme();
}

/**
 * The flavour picker: four swatches that recolour the whole admin by switching `--primary`
 * (the [data-flavor] blocks in index.css). A radiogroup, like the locale strip beside it.
 */
export function FlavorSwitch() {
  const { t } = useTranslation();
  const [flavor, setFlavor] = useState<Flavor>(readFlavor);

  useEffect(() => {
    document.documentElement.dataset.flavor = flavor;
    store(FLAVOR_KEY, flavor);
  }, [flavor]);

  return (
    <div
      role="radiogroup"
      aria-label={t('nav.flavor')}
      className="inline-flex items-center gap-1 rounded-lg bg-muted/60 px-1.5 py-1"
    >
      {FLAVORS.map((f) => {
        const selected = f.id === flavor;
        return (
          <button
            key={f.id}
            type="button"
            role="radio"
            aria-checked={selected}
            aria-label={f.label}
            title={f.label}
            onClick={() => setFlavor(f.id)}
            className={cn(
              'flex h-6 w-6 items-center justify-center rounded-full transition-transform duration-200',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-app',
              selected ? 'scale-110' : 'opacity-60 hover:scale-110 hover:opacity-100',
            )}
          >
            {/* The can's ring: a hollow circle when idle, filled when it is the chosen flavour. */}
            <span
              className="h-3.5 w-3.5 rounded-full border-[3px] transition-colors duration-200"
              style={{ borderColor: f.swatch, backgroundColor: selected ? f.swatch : 'transparent' }}
            />
          </button>
        );
      })}
    </div>
  );
}

/**
 * Light / dark. The switch is a WIPE, not a flip: the new theme opens as a circle growing out of
 * the button, via the View Transitions API. Where that API is missing (or motion is reduced) the
 * theme simply changes — the attribute write is the whole feature, the wipe is garnish.
 */
export function ThemeToggle() {
  const { t } = useTranslation();
  const [theme, setTheme] = useState<Theme>(readTheme);

  const toggle = (e: React.MouseEvent<HTMLButtonElement>) => {
    const next: Theme = theme === 'dark' ? 'light' : 'dark';
    const apply = () => {
      document.documentElement.dataset.theme = next;
      store(THEME_KEY, next);
      setTheme(next);
    };

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce || typeof document.startViewTransition !== 'function') {
      apply();
      return;
    }

    const box = e.currentTarget.getBoundingClientRect();
    const x = box.left + box.width / 2;
    const y = box.top + box.height / 2;
    // Far enough to reach the corner of the window furthest from the button.
    const radius = Math.hypot(Math.max(x, innerWidth - x), Math.max(y, innerHeight - y));

    void document.startViewTransition(apply).ready.then(() => {
      document.documentElement.animate(
        { clipPath: [`circle(0px at ${x}px ${y}px)`, `circle(${radius}px at ${x}px ${y}px)`] },
        {
          duration: 650,
          easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
          pseudoElement: '::view-transition-new(root)',
        },
      );
    });
  };

  const Icon = theme === 'dark' ? Moon : Sun;

  return (
    <button
      type="button"
      role="switch"
      aria-checked={theme === 'light'}
      aria-label={t('nav.theme')}
      title={t('nav.theme')}
      onClick={toggle}
      className={cn(
        'flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground transition-colors',
        'hover:bg-muted hover:text-foreground',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-app',
      )}
    >
      {/* Re-keyed so the incoming icon spins in rather than swapping. */}
      <Icon key={theme} className="h-4 w-4 animate-[bold-icon-in_0.45s_cubic-bezier(0.16,1,0.3,1)]" aria-hidden="true" />
    </button>
  );
}
